
import { safeGetItem, safeSetItem, registerMigration, validateData } from './storageService';

export const LIMIT_COUNT = 5;
export const LIMIT_WINDOW_MS = 10 * 60 * 1000; // 10 minutes
const STORAGE_KEY = 'legal_prompt_usage_timestamps';

export interface RateLimitResult {
  allowed: boolean;
  waitTimeMinutes?: number;
}

// Type guard for timestamps array
const isTimestampsArray = (data: unknown): data is number[] => {
  return Array.isArray(data) && data.every(item => typeof item === 'number');
};

// Register migration from version 0 (legacy format) to version 1
registerMigration<number[]>(STORAGE_KEY, 0, (oldData) => {
  // Legacy format was just an array of timestamps
  if (isTimestampsArray(oldData)) {
    return oldData;
  }
  // If it's not valid, return empty array
  console.log('Migrating rate limit data to new format');
  return [];
});

export const checkRateLimit = (): RateLimitResult => {
  try {
    const rawTimestamps = safeGetItem<number[]>(STORAGE_KEY, [], 'localStorage');
    
    // Validate the data
    let timestamps = validateData<number[]>(
      rawTimestamps, 
      isTimestampsArray, 
      []
    );
    
    const now = Date.now();

    // Filter out timestamps older than the window
    timestamps = timestamps.filter(t => typeof t === 'number' && now - t < LIMIT_WINDOW_MS);

    if (timestamps.length >= LIMIT_COUNT) {
      const oldest = timestamps[0];
      const waitMs = (oldest + LIMIT_WINDOW_MS) - now;
      const waitMinutes = Math.ceil(waitMs / 60000);
      return { 
        allowed: false, 
        waitTimeMinutes: waitMinutes > 0 ? waitMinutes : 1 
      };
    }

    return { allowed: true };
  } catch (e) {
    console.error("Rate limit check failed", e);
    return { allowed: true }; // Fail open if storage error
  }
};

export const recordUsage = (): void => {
  try {
    const rawTimestamps = safeGetItem<number[]>(STORAGE_KEY, [], 'localStorage');
    
    // Validate the data
    let timestamps = validateData<number[]>(
      rawTimestamps, 
      isTimestampsArray, 
      []
    );
    
    const now = Date.now();
    
    // Clean old
    timestamps = timestamps.filter(t => typeof t === 'number' && now - t < LIMIT_WINDOW_MS);
    // Add new
    timestamps.push(now);
    
    safeSetItem(STORAGE_KEY, timestamps, 'localStorage');
  } catch (e) {
    console.error("Rate limit record failed", e);
  }
};

/**
 * Reset rate limit (useful for testing or admin purposes)
 */
export const resetRateLimit = (): void => {
  safeSetItem(STORAGE_KEY, [], 'localStorage');
};

/**
 * Get remaining usage count
 */
export const getRemainingUsage = (): number => {
  try {
    const rawTimestamps = safeGetItem<number[]>(STORAGE_KEY, [], 'localStorage');
    const timestamps = validateData<number[]>(rawTimestamps, isTimestampsArray, []);
    const now = Date.now();
    const validTimestamps = timestamps.filter(t => now - t < LIMIT_WINDOW_MS);
    return Math.max(0, LIMIT_COUNT - validTimestamps.length);
  } catch {
    return LIMIT_COUNT;
  }
};
